import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { GroupedServiceSelect } from './GroupedServiceSelect';
import { DealGroupForSelector } from './services-tab.helpers';

function useStableArray(next: string[]): string[] {
    const ref = useRef(next);
    if (ref.current.length !== next.length || ref.current.some((v, i) => v !== next[i])) {
        ref.current = next;
    }
    return ref.current;
}

/**
 * Single grouped multi-select dropdown for service-asset mappings, combining
 * both ACTIVE and DELIVERED services. Services are grouped by deal (deals with
 * already-assigned services first, then newest first), and sorted alphabetically
 * within each deal.
 *
 * The user can both check (to add new mappings) and uncheck (to remove existing
 * ones); the parent diffs `selectedIds` against the current assignments on save
 * and fires POST/DELETE accordingly.
 */
export function ServiceSelector({
    selectedIds,
    onChange,
    groups,
    isLoading,
    autoOpen = false,
    onOpen,
}: {
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    groups: DealGroupForSelector[];
    isLoading: boolean;
    autoOpen?: boolean;
    onOpen?: () => void;
}) {
    const { t } = useTranslation();
    const stableSelected = useStableArray(selectedIds);

    return (
        <GroupedServiceSelect
            label={t('Add or remove services')}
            groups={groups}
            selected={stableSelected}
            onChange={onChange}
            isLoading={isLoading}
            autoOpen={autoOpen}
            onOpen={onOpen}
        />
    );
}
